import {
	AmbientLight,
	DirectionalLight,
	HemisphereLight,
	PointLight,
	RectAreaLight,
	Scene,
	SpotLight
} from 'three';

import { LightType, VisualizableLight } from './light-helper.types';

/**
 * Checks if an object is a visualizable light
 */
const isVisualizableLight = (child: unknown): child is VisualizableLight => {
	return (
		child instanceof AmbientLight ||
		child instanceof DirectionalLight ||
		child instanceof HemisphereLight ||
		child instanceof PointLight ||
		child instanceof RectAreaLight ||
		child instanceof SpotLight
	);
};

/**
 * Discovers all light instances in a scene
 *
 * @param scene Scene to traverse
 * @returns Array of discovered lights
 */
export const discoverLights = (scene: Scene): VisualizableLight[] => {
	const lights: VisualizableLight[] = [];

	scene.traverse((child) => {
		if (isVisualizableLight(child)) {
			lights.push(child);
		}
	});

	return lights;
};

/**
 * Discovers lights of a specific type
 *
 * @param scene Scene to traverse
 * @param lightType Type of light to find
 * @returns Array of matching lights
 */
export const discoverLightsByType = (
	scene: Scene,
	lightType: LightType
): VisualizableLight[] => {
	const lights: VisualizableLight[] = [];

	scene.traverse((child) => {
		if (!isVisualizableLight(child)) return;

		switch (lightType) {
			case LightType.Ambient:
				if (child instanceof AmbientLight) lights.push(child);
				break;
			case LightType.Directional:
				if (child instanceof DirectionalLight) lights.push(child);
				break;
			case LightType.Hemisphere:
				if (child instanceof HemisphereLight) lights.push(child);
				break;
			case LightType.Point:
				if (child instanceof PointLight) lights.push(child);
				break;
			case LightType.RectArea:
				if (child instanceof RectAreaLight) lights.push(child);
				break;
			case LightType.Spot:
				if (child instanceof SpotLight) lights.push(child);
				break;
		}
	});

	return lights;
};

/**
 * Discovers lights that match a name pattern
 *
 * @param scene Scene to traverse
 * @param pattern Name pattern to match (uses startsWith)
 * @returns Array of matching lights
 */
export const discoverLightsByName = (
	scene: Scene,
	pattern: string
): VisualizableLight[] => {
	const lights: VisualizableLight[] = [];

	scene.traverse((child) => {
		if (isVisualizableLight(child) && child.name.startsWith(pattern)) {
			lights.push(child);
		}
	});

	return lights;
};

/**
 * Checks if a light is currently "on" (visible and has intensity > 0)
 *
 * @param light Light to check
 * @returns true if light is on
 */
export const isLightOn = (light: VisualizableLight): boolean => {
	return light.visible && light.intensity > 0;
};

